import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi, groupApi } from '../lib/api';
import { ArrowLeft, Plus, X, Save, KeyRound } from 'lucide-react';

export default function UserDetail() {
  const { username } = useParams<{ username: string }>();
  const queryClient = useQueryClient();

  const [addGroupName, setAddGroupName] = useState('');
  const [addGroupError, setAddGroupError] = useState('');
  const [editingAttr, setEditingAttr] = useState<string | null>(null);
  const [attrValue, setAttrValue] = useState('');
  const [attrSaveError, setAttrSaveError] = useState('');
  const [resetDone, setResetDone] = useState(false);

  const { data: user, isLoading, error } = useQuery({
    queryKey: ['user', username],
    queryFn: () => userApi.get(username!),
    enabled: !!username,
  });

  const { data: allGroups } = useQuery({
    queryKey: ['groups'],
    queryFn: groupApi.list,
  });

  const addGroupMutation = useMutation({
    mutationFn: (groupName: string) => userApi.addToGroup(username!, groupName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', username] });
      setAddGroupName('');
      setAddGroupError('');
    },
    onError: (e: any) => setAddGroupError(e.message),
  });

  const removeGroupMutation = useMutation({
    mutationFn: (groupName: string) => userApi.removeFromGroup(username!, groupName),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user', username] }),
  });

  const updateAttrMutation = useMutation({
    mutationFn: (customAttributes: Record<string, string>) =>
      userApi.update(username!, { customAttributes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', username] });
      setEditingAttr(null);
      setAttrSaveError('');
    },
    onError: (e: any) => setAttrSaveError(e.message),
  });

  const resetMutation = useMutation({
    mutationFn: () => userApi.resetPassword(username!),
    onSuccess: () => setResetDone(true),
  });

  if (isLoading) return <div className="p-6 text-gray-500">Cargando...</div>;
  if (error || !user) return (
    <div className="p-6">
      <p className="text-red-600">No se encontró el usuario.</p>
      <Link to="/users" className="text-primary-600 hover:underline mt-2 inline-block">← Volver</Link>
    </div>
  );

  // Known custom attributes from the CognitoUser response
  const allCustomAttrs: [string, string][] = [
    ['custom:role', user.role ?? ''],
    ['custom:tenantId', user.tenantId ?? ''],
  ];
  const displayCustomAttrs = allCustomAttrs.filter(([, v]) => v !== '');
  const availableGroups = (allGroups || []).filter((g: any) => !user.groups?.includes(g.groupName));

  const statusColor = !user.enabled
    ? 'bg-red-100 text-red-800'
    : user.status === 'FORCE_CHANGE_PASSWORD'
    ? 'bg-yellow-100 text-yellow-800'
    : 'bg-green-100 text-green-800';

  const statusText = !user.enabled ? 'Inactivo' : user.status === 'FORCE_CHANGE_PASSWORD' ? 'Cambio de pass' : 'Activo';

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Link to="/users" className="text-gray-500 hover:text-gray-700">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-semibold text-gray-900">
          {user.givenName} {user.familyName}
        </h1>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
          {statusText}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Basic info */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Información</h2>
          <dl className="space-y-3">
            {[
              ['Email', user.email],
              ['ID', user.userId],
              ['Username', user.username],
              ['Email verificado', user.emailVerified ? 'Sí' : 'No'],
              ['Creado', user.createdAt ? new Date(user.createdAt).toLocaleString('es-AR') : '—'],
              ['Actualizado', user.updatedAt ? new Date(user.updatedAt).toLocaleString('es-AR') : '—'],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">{label}</dt>
                <dd className="text-sm text-gray-900 text-right">{value}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-4 pt-4 border-t">
            {resetDone ? (
              <p className="text-sm text-green-600">Email de reset enviado.</p>
            ) : (
              <button
                onClick={() => resetMutation.mutate()}
                disabled={resetMutation.isPending}
                className="inline-flex items-center text-sm text-orange-600 hover:text-orange-800 disabled:opacity-50"
              >
                <KeyRound className="h-4 w-4 mr-1" />
                {resetMutation.isPending ? 'Enviando...' : 'Resetear contraseña'}
              </button>
            )}
          </div>
        </div>

        {/* Groups */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Grupos</h2>
          <div className="space-y-2 mb-4">
            {user.groups?.length === 0 && <p className="text-sm text-gray-500">Sin grupos</p>}
            {user.groups?.map((g: string) => (
              <div key={g} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                <span className="text-sm text-gray-700">{g}</span>
                <button
                  onClick={() => removeGroupMutation.mutate(g)}
                  disabled={removeGroupMutation.isPending}
                  className="text-red-400 hover:text-red-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          {addGroupError && <p className="text-xs text-red-600 mb-2">{addGroupError}</p>}
          <div className="flex space-x-2">
            <select
              value={addGroupName}
              onChange={(e) => setAddGroupName(e.target.value)}
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">Seleccionar grupo...</option>
              {availableGroups.map((g: any) => (
                <option key={g.groupName} value={g.groupName}>{g.groupName}</option>
              ))}
            </select>
            <button
              onClick={() => addGroupName && addGroupMutation.mutate(addGroupName)}
              disabled={!addGroupName || addGroupMutation.isPending}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Custom Attributes */}
        <div className="bg-white shadow rounded-lg p-6 lg:col-span-2">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Atributos Personalizados</h2>
          {attrSaveError && <p className="text-xs text-red-600 mb-2">{attrSaveError}</p>}
          {displayCustomAttrs.length === 0 ? (
            <p className="text-sm text-gray-500">Este usuario no tiene atributos personalizados.</p>
          ) : (
            <div className="space-y-3">
              {displayCustomAttrs.map(([key, value]) => (
                <div key={key} className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-500 w-40 shrink-0">{key}</span>
                  {editingAttr === key ? (
                    <>
                      <input
                        value={attrValue}
                        onChange={(e) => setAttrValue(e.target.value)}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                      <button
                        onClick={() => updateAttrMutation.mutate({ [key]: attrValue })}
                        disabled={updateAttrMutation.isPending}
                        className="text-green-600 hover:text-green-800"
                      >
                        <Save className="h-4 w-4" />
                      </button>
                      <button onClick={() => setEditingAttr(null)} className="text-gray-400 hover:text-gray-600">
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm text-gray-900">{String(value)}</span>
                      <button
                        onClick={() => { setEditingAttr(key); setAttrValue(String(value)); setAttrSaveError(''); }}
                        className="text-indigo-500 hover:text-indigo-700 text-xs underline"
                      >
                        Editar
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
